"use client";

import type { NavActionCtaData } from "@homeinstead/shared/site-builder";
import type React from "react";
import type { ComponentProps } from "react";
import type { NavBarAppearance, NavigationMenuData } from "../../types";

import { cn } from "../../utils/classnames";
import { MobileNavigationMenu } from "./Menu/MobileNavigationMenu";
import { NavigationMenu } from "./Menu/NavigationMenu";
import { NavBarCTA } from "./NavBarCTA";
import { NavBarLogoLink } from "./NavBarLogoLink";
import { NavigationBottomPanel } from "./NavigationBottomPanel";
import { useScrollControlledVisibility } from "./use-scroll-controlled-visibility";
import { useScrolledPastViewport } from "./use-scrolled-past-viewport";

type NavBarProps = React.ComponentPropsWithoutRef<"nav"> & {
  appearance?: NavBarAppearance;
  menuItems: NavigationMenuData[];
  phoneNumber: string;
  primary?: NavActionCtaData | null;
  /**
   * Secondary CTA — renders in the large-viewport header next to primary.
   * Same value also feeds the small-viewport sticky placement unless
   * `stickyCta` is set; the parent resolves that fallback chain so this
   * component just receives what to render where.
   */
  secondary?: NavActionCtaData | null;
  /**
   * Resolved sticky CTA for the small-viewport bottom panel. When set, the
   * NavigationBottomPanel renders this CTA. When null, the bottom panel is
   * omitted from the layout entirely. The
   * parent is responsible for resolving the stickyCta override chain
   * (flag-matched variant → stickyCta.default → secondary fallback) before
   * passing this prop.
   */
  stickyCta?: NavActionCtaData | null;
  className?: string;
  disableLogoLink?: boolean;
  hidePrimaryCta?: boolean;
  visibilityControlledByScroll?: boolean;
  mobileTopOffset?: ComponentProps<typeof MobileNavigationMenu>["topOffset"];
};

function NavBar({
  appearance = "default",
  className,
  disableLogoLink = false,
  hidePrimaryCta = false,
  menuItems,
  mobileTopOffset,
  phoneNumber,
  primary = null,
  secondary = null,
  stickyCta = null,
  visibilityControlledByScroll = false,
  ...props
}: NavBarProps) {
  const shouldTrackViewport =
    visibilityControlledByScroll || secondary !== null || stickyCta !== null;
  const scrolledPastViewport = useScrolledPastViewport({
    enabled: shouldTrackViewport,
  });
  const { isHidden, navRef } = useScrollControlledVisibility({
    enabled: visibilityControlledByScroll,
    hasCrossedViewport: scrolledPastViewport,
  });
  const isCampaign = appearance === "campaign";
  const hasMenuItems = Boolean(menuItems && menuItems.length > 0);

  const logoLink = <NavBarLogoLink disableLogoLink={disableLogoLink} />;

  const desktopMenu = hasMenuItems ? (
    <NavigationMenu className={className} menuItems={menuItems} />
  ) : null;

  const mobileMenu = hasMenuItems ? (
    <MobileNavigationMenu
      appearance={appearance}
      menuItems={menuItems}
      topOffset={mobileTopOffset}
    />
  ) : null;

  const cta = (
    <NavBarCTA
      hidePrimaryCta={hidePrimaryCta}
      phoneNumber={phoneNumber}
      primary={primary}
      scrolledPastViewport={scrolledPastViewport}
      secondaryCta={secondary}
    />
  );

  return (
    <>
      <nav
        // Appearance theming for descendants (logo, menu, CTA, hamburger) is
        // driven by `data-appearance` + `group/navbar` so leaf components use
        // `group-data-[appearance=…]/navbar:` instead of prop-drilled
        // `isCampaign` class switches.
        //
        // useScrollControlledVisibility drives mobile-only hiding. When
        // visibilityControlledByScroll is opted in AND the user has scrolled
        // past the hero AND focus is not inside the nav, `isHidden` flips true
        // and the nav slides up out of view on viewports below `md`.
        className={cn(
          "navbar group/navbar",
          "relative z-40 w-full shadow-xl",
          // Host shell colors stay on `isCampaign` so a missing token can't
          // silently leave the default dark bar. Descendants theme via
          // `data-appearance` + `group-data-[appearance=…]/navbar:`.
          isCampaign
            ? "bg-white text-new-beginnings-base"
            : "bg-new-beginnings-base text-support-base",
          "translate-y-0",
          "motion-safe:[transition-property:transform,visibility]",
          "motion-safe:[transition-duration:200ms,0s]",
          "motion-safe:[transition-timing-function:ease-out,linear]",
          {
            "motion-safe:max-md:invisible motion-safe:max-md:-translate-y-full motion-safe:max-md:[transition-delay:0ms,200ms]":
              isHidden,
          },
          className,
        )}
        data-appearance={appearance}
        ref={navRef}
        {...props}
      >
        <div
          className={cn(
            "mx-auto flex h-20 max-w-screen-hd items-center gap-4 p-4",
            !isCampaign && "justify-between",
          )}
        >
          {isCampaign ? (
            <>
              {/* Below xl there is no centered menu — keep logo/CTA shrink-wrapped
                  (ml-auto end) so hamburger + phone are never half-clipped.
                  From xl up, equal flex-1 wings center the in-flow menu; min-w-0
                  + overflow-hidden clip wing content instead of painting over it. */}
              <div
                className="flex shrink-0 justify-start xl:min-w-0 xl:flex-1 xl:overflow-hidden"
                data-navbar-campaign-wing="start"
              >
                {logoLink}
              </div>

              {hasMenuItems ? (
                <div
                  className="relative z-[1] hidden shrink-0 xl:flex"
                  data-navbar-desktop-menu="centered"
                >
                  {desktopMenu}
                </div>
              ) : null}

              <div
                className="ml-auto flex shrink-0 items-center justify-end gap-4 xl:ml-0 xl:min-w-0 xl:flex-1 xl:overflow-hidden"
                data-navbar-campaign-wing="end"
              >
                {hasMenuItems ? (
                  <div className="xl:hidden">{mobileMenu}</div>
                ) : null}
                {cta}
              </div>
            </>
          ) : (
            <>
              {logoLink}

              {hasMenuItems ? (
                <div className="ml-auto flex items-center">
                  <div
                    className="hidden xl:block"
                    data-navbar-desktop-menu="trailing"
                  >
                    {desktopMenu}
                  </div>

                  <div className="block flex-1 xl:hidden">{mobileMenu}</div>
                </div>
              ) : null}

              {cta}
            </>
          )}
        </div>
      </nav>

      <NavigationBottomPanel
        phoneNumber={phoneNumber}
        scrolledPastViewport={scrolledPastViewport}
        stickyCta={stickyCta}
      />
    </>
  );
}

export type { NavBarProps };

export { NavBar };
